import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth';
import { usePublicConfig } from '@/lib/public-config';
import { LandingPage } from '@/pages/landing';

/** Params that must survive when an external link lands on the app root. */
const FORWARD_PARAMS = ['url', 'website', 'next', 'ref', 'src'] as const;

export function forwardedSignupSearch(search: string): string | null {
  const incoming = new URLSearchParams(search);
  const out = new URLSearchParams();
  for (const key of FORWARD_PARAMS) {
    const v = incoming.get(key);
    if (v) out.set(key, v);
  }
  const hasIntent = out.has('url') || out.has('website') || out.has('next');
  return hasIntent ? out.toString() : null;
}

/**
 * App `/`: signed-in users go to /app. Logged-out visitors see the in-app landing
 * unless MARKETING_ORIGIN is set (optional future external site redirect).
 * Links carrying ?url= or ?next= go to /signup with those params instead.
 */
export function RootRedirect() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { data: config, isLoading: configLoading, isError } = usePublicConfig();

  const ready = !loading && (!configLoading || isError);
  const origin = (config?.marketingOrigin ?? '').trim();
  // Standalone CGK: blank MARKETING_ORIGIN (or config error) → in-app landing.
  const disabled = !origin || isError;

  useEffect(() => {
    if (!ready) return;
    if (user?.user) {
      navigate({ to: '/app', replace: true });
      return;
    }
    const forwarded = forwardedSignupSearch(window.location.search);
    if (forwarded) {
      window.location.replace(`/signup?${forwarded}`);
      return;
    }
    if (!disabled) window.location.replace(origin);
  }, [ready, user?.user, disabled, origin, navigate]);

  if (ready && user?.user) {
    return (
      <div className="flex min-h-screen items-center justify-center text-text-secondary" role="status">
        Opening your kit…
      </div>
    );
  }
  if (ready && disabled && !user?.user) return <LandingPage />;
  return (
    <div className="flex min-h-screen items-center justify-center text-text-secondary" role="status">
      Loading…
    </div>
  );
}
